import '../styles/ResearchPage.css';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../authentication/AuthContext';
import { Box, Typography, Tabs, Tab, Button, Collapse, CircularProgress } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import ResearchDescription from './ResearchDescription'; // Import the new component
import Step1Survey from './SurveyStep/Step1Survey.js';
import Step2Personas from './PersonasStep/Step2Personas';
import Step3Responses from './ResponsesStep/Step3Responses';
import Step4FinalReview from './FinalStep/Step4FinalReview.js';
import { api } from '../services/api';
import { serverDataToTopics, mapServerResearchToLocal, createTopic, getProgress } from '../services/apiUtils';
import { ThemeProvider } from '@mui/material/styles';
import ResearchTheme from '../styles/Theme.js';
import TopicsList from './Topics/TopicsList.js';
import { dataApi } from '../services/dataApi.js';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import SharedSnapshotBanner from '../components/SharedSnapshotBanner';


function ResearchPage({ readOnly = false, initialData = null }) {
  const { sub } = useAuth(); 
  
  // State
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  
  // Hide sidebar by default if readOnly
  const [isSidebarVisible, setIsSidebarVisible] = useState(!readOnly);
  
  const [tabIndex, setTabIndex] = useState(0);
  
  // If initialData exists, we are not loading. If not readOnly, we assume we need to load.
  const [isLoading, setIsLoading] = useState(!initialData && !readOnly);
  
  const [showDescription, setShowDescription] = useState(false);
  const [unsavedSurveyChanges, setUnsavedSurveyChanges] = useState(false);
  const [showMobileWarning, setShowMobileWarning] = useState(false);
  
  const [isTopicLoading, setIsTopicLoading] = useState(false);
  const [cursor, setCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);
  
  const [userData, setUserData] = useState(createTopic({ userID: sub || '' }));
  const [progress, setProgress] = useState(0);
  const [triggerResetSurveyLoading, setTriggerResetSurveyLoading] = useState(false);

  const isMobile = useMediaQuery('(max-width:768px)');
  const mainContentRef = useRef(null);



  const updateTopic = (updatedFields) => {
    if (readOnly || !selectedTopic) return; // nothing selected -> nothing to update

    if (selectedTopic.researchID !== updatedFields.researchID) {
      setTopics(prev => [...prev, updatedFields]);
    }

    const updatedSelected = { ...selectedTopic, ...updatedFields };
    setSelectedTopic(updatedSelected);

    // Reflect the change in the sidebar list as well
    setTopics(prev => prev.map(t =>
      t.researchID === updatedSelected.researchID ? { ...t, ...updatedFields } : t
    ));
  };

  useEffect(() => {
    // fetch progress whenever a new research gets selected
    setProgress(getProgress(userData));
  }, [userData.researchID]);

  useEffect(() => {
    // Show warning dialog if user is on mobile
    if (isMobile) {
      setShowMobileWarning(true);
    }
  }, [isMobile]);

  // --- Helper to Adapt Data ---
  const processAndSetTopics = useCallback((aggregateData) => {
    let dataForUtils = aggregateData;

    // ADAPTER: If data comes from Shared Link, it's a single object, 
    // but apiUtils expects arrays.
    if (aggregateData.research && !aggregateData.researches) {
        dataForUtils = {
            researches: [aggregateData.research],
            personas: aggregateData.personas || [],
            responses: aggregateData.responses || []
        };
    }

    const fullTopics = serverDataToTopics(dataForUtils);
    setTopics(fullTopics);
    return fullTopics;
  }, []);

  // --- Initial Load Effect ---
  useEffect(() => {
    
    // 1. GUEST MODE
    if (readOnly && initialData) {
        const fullTopics = processAndSetTopics(initialData);
        if (fullTopics.length > 0) {
            const topic = fullTopics[0];
            
            // Map Analysis Data if present in the public response
            if (initialData.analysis) {
              topic.surveyAnalysis = initialData.analysis.survey;
              topic.questionAnalysis = initialData.analysis.questions;
            }

            setSelectedTopic(topic);
            setUserData(topic);
            setProgress(getProgress(topic));
        }
        setIsLoading(false);
        return; 
    }

    // 2. OWNER MODE
    if (sub && !readOnly) {
        setIsLoading(true);
        dataApi.fetchPage()
        .then((aggregateData) => {
            const fullTopics = processAndSetTopics(aggregateData);
            setCursor(aggregateData.nextCursor);

            setIsLoading(false);
        })
        .catch((error) => {
            console.error('Error loading initial data:', error);
            setIsLoading(false);
        });
    }

  }, [sub, readOnly, initialData, processAndSetTopics]);



  /* -------- fetch more -------- */
  const fetchMore = useCallback(async () => {
    if (!cursor || loadingMore || readOnly) return;          // nothing left or already working

    setLoadingMore(true);

    dataApi.fetchPage({cursor})
    .then((aggregateData) => {

      const fullTopics = serverDataToTopics(aggregateData);
      
      setTopics(prevTopics => [...prevTopics, ...fullTopics]);
      setCursor(aggregateData.nextCursor);
      setLoadingMore(false);
    })
    .catch((error) => {
      console.error('Error loading initial data:', error);
      setLoadingMore(false);
    });

  }, [cursor, loadingMore, readOnly]);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore) fetchMore();
      },
      { threshold: 0.3,       // any overlap
      }
    );

    io.observe(sentinelRef.current);
    return () => io.disconnect();
  }, [fetchMore, loadingMore]);


  const confirmDiscardChanges = () => {
    if (readOnly) return true;
    if (unsavedSurveyChanges) {
      const proceed = window.confirm(
        'You have unsaved changes in your survey. Continuing will discard those changes. Do you want to proceed?'
      );
      if (proceed) {
        setUnsavedSurveyChanges(false);
      }
      return proceed;
    }
    return true;
  };


  const handleTabChange = (event, newValue) => {
    if (tabIndex === 0 && newValue !== 0) {
      if (!confirmDiscardChanges()) return;
    }
    setTabIndex(newValue);
    if (mainContentRef.current) mainContentRef.current.scrollTop = 0;
  };

  // Topic handlers
  const handleNewResearchClick = () => {
    if (readOnly) return;
    if (!confirmDiscardChanges()) {
      return; // User aborted action
    }

    setTriggerResetSurveyLoading(prev => !prev);

    const newTopic = createTopic({ userID: sub });

    setSelectedTopic(newTopic);
    setTabIndex(0);
    setProgress(0);
    setUserData(newTopic);
    setIsSidebarVisible(false); // Close the sidebar
  };

  const handleTopicSelect = async (topic) => {
    if (!confirmDiscardChanges()) {
      return; // User aborted navigation
    }
    console.log('🔍 handleTopicSelect called with topic:', topic);
    console.log("progress is", progress);
    
    // Set loading state to true immediately when a topic is clicked
    setIsTopicLoading(true);
        
    setSelectedTopic(topic);
    setUserData(topic);
    setTabIndex(0); // Show the first tab when selecting a topic
    setProgress(getProgress(topic));
    setIsTopicLoading(false);

  };

  const handleDeleteTopic = async (researchID) => {
    if (readOnly) return;
    try {
      await api.deleteResearch(sub, researchID); 
      const updatedTopics = topics.filter(t => t.researchID !== researchID);
      setTopics(updatedTopics);
      if (selectedTopic?.researchID === researchID) {
        setSelectedTopic(null);
      }
    } catch (error) {
      console.error('Error deleting research:', error);
    }
  }
  
  const handleDuplicateResearch = async (researchID, newTitle) => {
    if (readOnly) return;
    console.log("IN HANDLE DUPLICATE RESEARCH, userData is", userData);
    console.log("IN HANDLE DUPLICATE RESEARCH, selectedTopic is", selectedTopic);


    
    
    try {
      setIsLoading(true);
      const result = await api.duplicateResearch(sub, researchID, newTitle);
      console.log("duplicate research api result ", result);

      const research = mapServerResearchToLocal(result);
      console.log("duplicate research local mapping result ", result);
      const newTopic = {...selectedTopic, title: research.title, researchID: research.researchID};

      console.log("new topic after duplicate research is",newTopic);

      setTopics(prevTopics => [...prevTopics, newTopic]);
      handleTopicSelect(newTopic);
      
      // Show success message (you could add a snackbar/toast for this)
    } catch (error) {
      // console.error('Error duplicating research:', error);
      // You could show an error message to the user here
    } finally {
      setIsLoading(false);
      console.log("DONE WITH DUPLICATING, SELECTED TOPIC IS NOW", selectedTopic);
    }
  };

  const handleRenameClick = async (researchID, newTitle) => {
    if (readOnly) return;
    try {
      await api.updateResearch(sub, researchID, { title: newTitle });

      // Update the topics list and selected topic
      setTopics(prevTopics => prevTopics.map(t => t.researchID === researchID ? { ...t, title: newTitle } : t));

      if (selectedTopic?.researchID === researchID) {
        setSelectedTopic(prev => ({ ...prev, title: newTitle }));
      }
      if (userData?.researchID === researchID) {
        setUserData(prev => ({ ...prev, title: newTitle}));
      }
    } catch (error) {
      console.error('Error renaming research:', error);
    }
  };

  return (
    <ThemeProvider theme={ResearchTheme}>
      <div className="research-page">
        {!readOnly && (
        <aside className={`sidebar ${isSidebarVisible ? 'visible' : 'collapsed'}`}>
          <div className="sidebar-header">
            <h2>Your Researches</h2>
          </div>
          <button 
            className="button standout-button research-button" 
            style={{ maxWidth: 'none', width: '90%' }} 
            onClick={handleNewResearchClick}
          >
            New Research
          </button>


          <TopicsList
            topics={topics}
            onTopicClick={handleTopicSelect}
            selectedTopic={selectedTopic}
            isLoading={isLoading}
            onDeleteTopic={handleDeleteTopic}
            onRenameTopic={handleRenameClick}
            onDuplicateTopic={handleDuplicateResearch}
            sentinelRef={cursor ? sentinelRef : null}
            loadingMore={loadingMore}
          />
        </aside>
        )}


        {!readOnly && (

        <button 
          className="toggle-sidebar"
          onClick={() => setIsSidebarVisible(!isSidebarVisible)}
        >
          {isSidebarVisible ? '←' : '→'}
        </button>
        )}

        <main ref={mainContentRef} className={`research-content ${isSidebarVisible ? 'visible' : 'collapsed'} ${readOnly ? 'full-width' : ''}`}>
          
          {readOnly && <SharedSnapshotBanner />}

          {isTopicLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
              <Typography variant="h6" sx={{ ml: 2 }}>
                Loading research...
              </Typography>
            </Box>
          ) : selectedTopic ? (
            <Box sx={{ p: 3, marginLeft: '2rem', marginRight: '2rem' }}>
              <Typography variant="h4" gutterBottom align="center">
                {selectedTopic.title}
              </Typography>
              {(userData.surveyStatus === 'COMPLETED' || 
                  (selectedTopic && selectedTopic.researchID && selectedTopic.description)) && (
                  <Box sx={{ position: 'relative' }}>
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'flex-end',
                      mb: 1
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        <Button
                          onClick={() => setShowDescription(!showDescription)}
                          startIcon={showDescription ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          size="small"
                        >
                          {showDescription ? 'Hide Prompt' : 'Show Prompt'}
                        </Button>
                      </Box>
                    </Box>
                    <Collapse in={showDescription}>
                      <ResearchDescription description={selectedTopic.description || userData.description} />
                    </Collapse>
                  </Box>
                )}
              <Box sx={{ width: '100%', marginBottom: 3 }}>
                <Box sx={{ overflowX: 'auto', whiteSpace: 'nowrap', paddingRight: '15%'}}>
                  <Tabs 
                    value={tabIndex}
                    onChange={handleTabChange}
                    centered
                    sx={{
                      '& .MuiTabs-indicator': {
                        backgroundColor: 'var(--tertiary-color)',
                        textColor: "var(--tertiary-color)",
                      },
                      '& .Mui-selected': {
                        color: "var(--tertiary-color)",
                      },
                    }}
                  >
                    <Tab label="Survey" tab-id="tab-survey" sx={{ marginLeft: '15%', width: '15%' }} />
                    <Tab label="Personas" tab-id="tab-personas" disabled={progress < 2 && !readOnly} sx={{ width: '15%' }} />
                    <Tab label="Responses" tab-id="tab-responses" disabled={progress < 3 && !readOnly} sx={{ width: '15%' }} />
                    <Tab label="Review" tab-id="tab-review" disabled={progress < 4 && !readOnly} sx={{ width: '15%'}} />
                  </Tabs>
                </Box>

                {/* Only show research description after survey has been generated or for existing topics */}
                
                {/* Tab content */}
                <Box sx={{ marginTop: 3, marginBottom: 10, }}>
                  {tabIndex === 0 && (
                    <Step1Survey
                      userData={userData}
                      setUserData={setUserData}
                      onTopicUpdate={updateTopic}
                      setTabIndex={setTabIndex}
                      progress={progress}
                      setProgress={setProgress}
                      unsavedSurveyChanges={unsavedSurveyChanges}
                      setUnsavedSurveyChanges={setUnsavedSurveyChanges}
                      resetLoading={triggerResetSurveyLoading}
                      readOnly={readOnly}
                    />
                  )}
                  {tabIndex === 1 && (
                    <Step2Personas
                      userData={userData}
                      setUserData={setUserData}
                      onTopicUpdate={updateTopic}
                      setTabIndex={setTabIndex}
                      progress={progress}
                      setProgress={setProgress}
                      readOnly={readOnly}
                    />
                  )}
                  {tabIndex === 2 && (
                    <Step3Responses
                      userData={userData}
                      setUserData={setUserData}
                      onTopicUpdate={updateTopic}
                      setTabIndex={setTabIndex}
                      progress={progress}
                      setProgress={setProgress}
                      readOnly={readOnly}
                    />
                  )}
                  {tabIndex === 3 && (
                    <Step4FinalReview
                      userData={userData}
                      readOnly={readOnly}
                    />
                  )}
                </Box>
              </Box>
            </Box>
          ) : (
            !readOnly && (

            <Box sx={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center', 
              minHeight: '50vh',
              width: '100%',
            }}>
              <Typography 
                variant="h5" 
                align="center" 
              >
                Select a research topic or create a new one to begin
              </Typography>
              <button 
                className="button standout-button research-button" 
                onClick={handleNewResearchClick}
              >
                New Research
              </button>
            </Box>
            )
          )}
        </main>
      </div>

      {/* Mobile Warning Dialog */}
      <Dialog
        open={showMobileWarning}
        onClose={() => setShowMobileWarning(false)}
        aria-labelledby="mobile-warning-dialog-title"
      >
        <DialogTitle id="mobile-warning-dialog-title" sx={{ textAlign: 'center' }}>
          Desktop Recommended
        </DialogTitle>
        <DialogContent>
          <Typography>
            For the best experience with our research tools, we recommend using a desktop or laptop computer. 
            The survey creation and analysis features are optimized for larger screens.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button 
            variant="contained" 
            onClick={() => setShowMobileWarning(false)}
            sx={{ 
              backgroundColor: 'var(--tertiary-color)',
              '&:hover': {
                backgroundColor: 'var(--darker-color)',
              }
            }}
          >
            Continue Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
}

export default ResearchPage;
